import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia,
  Stack,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip
} from '@mui/material';
import { 
  Search as SearchIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import ApiService from '../../services/api';
import Avatar from '@mui/material/Avatar';

const Home = () => {
  const [featuredEmployers, setFeaturedEmployers] = useState([]);
  const [featuredSeekers, setFeaturedSeekers] = useState([]);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [ads, setAds] = useState([]);
  const [adIndex, setAdIndex] = useState(0);
  const [loadingFeatured, setLoadingFeatured] = useState(false);
  const [featuredError, setFeaturedError] = useState(null);

  useEffect(() => {
    const loadFeatured = async () => {
      setLoadingFeatured(true);
      try {
        const employersRes = await ApiService.getFeaturedEmployers(6);
        const seekersRes = await ApiService.getFeaturedJobSeekers(6);
        // Fetch boosted-active jobs for home page prominence
        const jobsRes = await ApiService.getJobs({ limit: 24, sortBy: 'createdAt', sortOrder: 'desc', boostStatus: 'active' });
        // Fetch ads for carousel
        const adsRes = await ApiService.getAds();
        const rawEmployers = employersRes?.employers || [];
        const rawSeekers = seekersRes?.jobSeekers || [];
        const jobsPayload = jobsRes?.jobs || jobsRes?.data?.jobs || jobsRes?.data?.data?.jobs || [];

        // Show only boosted items on the landing page
        const isBoostActive = (boosted, boostExpiresAt) => {
          if (boosted === true || boosted === 1) {
            if (!boostExpiresAt) return true;
            try {
              return new Date(boostExpiresAt).getTime() > Date.now();
            } catch (e) {
              return true;
            }
          }
          return false;
        };

        const boostedEmployers = rawEmployers.filter(e => isBoostActive(e?.boosted, e?.boostExpiresAt));
        const boostedSeekers = rawSeekers.filter(s => isBoostActive(s?.boosted, s?.boostExpiresAt));

        // For jobs: show ONLY actively boosted jobs on the home page
        const boostedJobsOnly = (Array.isArray(jobsPayload) ? jobsPayload : [])
          .filter(j => isBoostActive(j?.boosted, j?.boostExpiresAt));
        const sortedBoostedJobs = [...boostedJobsOnly].sort((a, b) => {
          // boosted already guaranteed; secondary sort featured, then newest
          if (!!a?.featured !== !!b?.featured) return a?.featured ? -1 : 1;
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });

        setFeaturedEmployers(boostedEmployers);
        setFeaturedSeekers(boostedSeekers);
        setFeaturedJobs(sortedBoostedJobs);
        const apiOrigin = (ApiService.baseURL || '').replace(/\/api$/, '');
        const resolveMedia = (u) => {
          if (!u) return '';
          try { return u.startsWith('/') ? (apiOrigin + u) : u; } catch { return u; }
        };
        const normalizedAds = (adsRes?.ads || adsRes?.data?.ads || []).map(a => ({
          ...a,
          mediaUrl: resolveMedia(a.mediaUrl || a.imageUrl || ''),
          imageUrl: resolveMedia(a.imageUrl || a.mediaUrl || ''),
        }));
        setAds(normalizedAds);
      } catch (err) {
        console.error('Failed to load featured profiles', err);
        setFeaturedError('Failed to load featured profiles');
      } finally {
        setLoadingFeatured(false);
      }
    };
    loadFeatured();
  }, []);

  useEffect(() => {
    if (!ads || ads.length === 0) return;
    const timer = setInterval(() => {
      setAdIndex((prev) => (prev + 1) % ads.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [ads]);
  const [stats, setStats] = useState({ jobs: 0, employers: 0, users: 0, jobSeekers: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await ApiService.getPublicStats();
        setStats({
          jobs: Number(data.jobs) || 0,
          employers: Number(data.employers) || 0,
          users: Number(data.users) || 0,
          jobSeekers: Number(data.jobSeekers) || 0,
        });
      } catch (error) {
        console.error('Failed to load public stats:', error);
      }
    };
    fetchStats();
  }, []);

  const displayCount = (n) => {
    const num = Number(n) || 0;
    return new Intl.NumberFormat('en-US').format(num);
  };
  return (
    <Box>
      {/* Hero Section */}
      <Box 
  sx={{
    bgcolor: '#cf1130',
          color: 'white',
          py: 8,
          mb: 6,
          borderRadius: { xs: 0, sm: 2 },
          boxShadow: 3
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" gutterBottom>
                Find Your Perfect Match
              </Typography>
              <Typography variant="h5" paragraph>
                Connect with employers, job seekers, and service providers all in one place.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                  component={RouterLink}
                  to="/register"
                >
                  Get Started
                </Button>
                <Button 
                  variant="outlined" 
                  color="inherit" 
                  size="large"
                  component={RouterLink}
                  to="/about"
                >
                  Learn More
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={6} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" color="text.primary" gutterBottom>
                  Search Jobs, Employers, and Job Seekers
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Job title, employer, or job seeker..."
                  variant="outlined"
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="primary"
                      component={RouterLink}
                      to="/jobs"
                    >
                      Find Jobs
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      color="primary"
                      component={RouterLink}
                      to="/employers"
                    >
                      Browse Employers
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      color="primary"
                      component={RouterLink}
                      to="/job-seekers"
                    >
                      Browse Job Seekers
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
      </Container>
    </Box>

    {/* Ads Carousel */}
    {ads.length > 0 && (
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Card sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
          {/* Current Ad */}
          {(() => {
            const current = ads[adIndex] || {};
            const mediaUrl = current.mediaUrl || current.imageUrl || '';
            const isVideo = (current.resourceType === 'video') || (current.mediaType === 'video') || /\/(video)\//.test(mediaUrl);
            const link = current.linkUrl || '';
            const content = isVideo ? (
              <CardMedia
                component="video"
                src={mediaUrl}
                autoPlay
                muted
                loop
                controls={false}
                sx={{ height: { xs: 160, sm: 220, md: 280 }, objectFit: 'cover' }}
              />
            ) : (
              <CardMedia
                component="img"
                image={mediaUrl}
                alt={current.title || 'Advertisement'}
                sx={{ height: { xs: 160, sm: 220, md: 280 }, objectFit: 'cover' }}
              />
            );
            return link ? (
              <a href={link} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                {content}
              </a>
            ) : content;
          })()}
          {/* Controls */}
          <Box sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={() => setAdIndex((adIndex - 1 + ads.length) % ads.length)}>Prev</Button>
          </Box>
          <Box sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={() => setAdIndex((adIndex + 1) % ads.length)}>Next</Button>
          </Box>
          {/* Dots */}
          <Box sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1 }}>
            {ads.map((_, i) => (
              <Box key={i} onClick={() => setAdIndex(i)} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: i === adIndex ? 'primary.main' : 'grey.400', cursor: 'pointer' }} />
            ))}
          </Box>
        </Card>
      </Container>
    )}

    {/* Featured Jobs (boosted-only) */}
    {featuredJobs.length > 0 && (
    <Container maxWidth="lg" sx={{ mb: 8 }}>
      <Typography variant="h4" gutterBottom>
        Featured Jobs
      </Typography>
      {featuredError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {featuredError}
        </Typography>
      )}
      <Grid container spacing={3}>
        {loadingFeatured ? (
          <Typography variant="body1">Loading...</Typography>
        ) : (
          featuredJobs.slice(0, 6).map((job) => {
            const companyName = job?.employer?.companyName || job?.companyName || '—';
            const locationText = job?.location?.city || job?.location?.country || job?.workLocation || '—';
            const logoSrc = job?.employer?.avatar || job?.employer?.logo || '';
            const jobId = job?._id || job?.id || '';
            const boostActive = job?.boosted === true || job?.boosted === 1;
            return (
              <Grid item xs={12} sm={6} md={4} key={jobId}>
                <Card sx={{ ...(boostActive ? { backgroundColor: 'rgba(255, 243, 224, 0.6)' } : {}) }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar src={logoSrc} alt={companyName} sx={{ mr: 2 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{job?.title || 'Untitled Job'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {companyName} • {locationText}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {boostActive && (<Chip label="Boosted" color="secondary" size="small" sx={{ mr: 1 }} />)}
                      </Box>
                    </Box>
                    <Button 
                      size="small" 
                      component={RouterLink} 
                      to={`/jobs/${jobId}`} 
                      endIcon={<ArrowForwardIcon />}
                    >
                      View
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Button component={RouterLink} to="/jobs" endIcon={<ArrowForwardIcon />}>
          Browse Jobs
        </Button>
      </Box>
    </Container>
    )}

    {/* Features Section */}
    <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom>
            How It Works
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Our platform connects employers, job seekers, and service providers in a seamless ecosystem
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {/* For Employers */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <WorkIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
                <Typography gutterBottom variant="h5" component="h3">
                  For Employers
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Post job listings, find qualified candidates, and manage the hiring process all in one place.
                </Typography>
                <Button 
                  endIcon={<ArrowForwardIcon />}
                  component={RouterLink}
                  to="/employer/post-job"
                >
                  Post a Job
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* For Job Seekers */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <PersonIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
                <Typography gutterBottom variant="h5" component="h3">
                  For Job Seekers
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Create your CV, apply for jobs, and showcase your skills to potential employers.
                </Typography>
                <Button 
                  endIcon={<ArrowForwardIcon />}
                  component={RouterLink}
                  to="/jobseeker/create-cv"
                >
                  Create Your CV
                </Button>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>

      {/* Featured Employers (boosted-only) */}
      {featuredEmployers.length > 0 && (
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h4" gutterBottom>
          Featured Employers
        </Typography>
        {featuredError && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {featuredError}
          </Typography>
        )}
        <Grid container spacing={3} alignItems="stretch">
          {loadingFeatured ? (
            <Typography variant="body1">Loading...</Typography>
          ) : (
            featuredEmployers.map((emp) => (
              <Grid item xs={12} sm={6} md={4} key={emp._id} sx={{ display: 'flex' }}>
                <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', ...((emp?.boosted === true || emp?.boosted === 1) ? { backgroundColor: 'rgba(255, 243, 224, 0.6)' } : {}) }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={emp.avatar} alt={emp.firstName} sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6">
                          {emp.employerProfile?.companyName || `${emp.firstName || ''} ${emp.lastName || ''}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {emp.location?.city || emp.location?.country || '—'}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {(emp?.boosted === true || emp?.boosted === 1) && (<Chip label="Boosted" color="secondary" size="small" sx={{ mr: 1 }} />)}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button component={RouterLink} to="/employers" endIcon={<ArrowForwardIcon />}>
            Browse Employers
          </Button>
        </Box>
      </Container>
      )}

      {/* Featured Job Seekers (boosted-only) */}
      {featuredSeekers.length > 0 && (
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h4" gutterBottom>
          Featured Job Seekers
        </Typography>
        <Grid container spacing={3} alignItems="stretch">
          {loadingFeatured ? (
            <Typography variant="body1">Loading...</Typography>
          ) : (
            featuredSeekers.map((seeker) => (
              <Grid item xs={12} sm={6} md={4} key={seeker._id} sx={{ display: 'flex' }}>
                <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', ...((seeker?.boosted === true || seeker?.boosted === 1) ? { backgroundColor: 'rgba(255, 243, 224, 0.6)' } : {}) }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={seeker.avatar} alt={seeker.firstName} sx={{ mr: 2 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6">
                          {`${seeker.firstName || ''} ${seeker.lastName || ''}`.trim()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {seeker.jobSeekerProfile?.profession || seeker.location?.city || '—'}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {(seeker?.boosted === true || seeker?.boosted === 1) && (<Chip label="Boosted" color="secondary" size="small" sx={{ mr: 1 }} />)}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 'auto' }}>
                      <Button 
                        size="small" 
                        component={RouterLink} 
                        to={`/job-seekers/${seeker._id}`} 
                        endIcon={<ArrowForwardIcon />}
                      >
                        View
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button component={RouterLink} to="/job-seekers" endIcon={<ArrowForwardIcon />}>
            Browse Job Seekers
          </Button>
        </Box>
      </Container>
      )}

      {/* Stats Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 6, mb: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={4} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary" gutterBottom>
                  {displayCount(stats.jobs)}+
                </Typography>
                <Typography variant="h6">Approved Jobs</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary" gutterBottom>
                  {displayCount(stats.employers)}+
                </Typography>
                <Typography variant="h6">Registered Employers</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary" gutterBottom>
                  {displayCount(stats.jobSeekers)}+
                </Typography>
                <Typography variant="h6">Job Seekers</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary" gutterBottom>
                  98%
                </Typography>
                <Typography variant="h6">Satisfaction Rate</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2,
            background: 'linear-gradient(45deg, #cf1130 30%, #cf1130 90%)',
            color: 'white'
          }}
        >
          <Typography variant="h4" gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="subtitle1" paragraph sx={{ mb: 4 }}>
-            Join thousands of users who have found jobs, hired talent, or grown their service business.
+            Join thousands of users who have found jobs and hired top talent.
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            sx={{ 
              bgcolor: 'white', 
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}
            component={RouterLink}
            to="/register"
          >
            Sign Up Now
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default Home;